<template>
  <div class="report-list">
    <s-table
      class="report-list__table"
      :items="reports"
      :fields="fields"
      :sort-by="sortBy.field"
      :sort-desc="sortBy.desc"
      :busy="loading"
      @sort-changed="sort($event)"
    >
      <template #cell(installations)="data">
        <list-aggregator
          suppressed-label-path="reports.list.suppressed-installations"
          :modal-title="data.item.name"
          :modal-table-items="data.item.installations"
          :modal-table-header="$t('entities.installation')"
        />
      </template>
      <template #cell(assets)="data">
        <list-aggregator
          suppressed-label-path="reports.list.suppressed-assets"
          :modal-title="data.item.name"
          :modal-table-items="showAllAssetsWithType(data.item.assets)"
          :modal-table-header="tableHeadersAssets"
          :multipleColumns="multipleColumnsFlag"
          name-column="ativo"
        />
      </template>
      <template #cell(period)="data">
        <span v-if="isPeriodPersonalized(data.item.period)">
          {{ data.item.initialDate | dateTimeFilter }}
          - {{ data.item.finalDate | dateTimeFilter }}
        </span>
        <span v-else>
          <span>{{ $t('reports.period.last-days', {
              days: data.item.period
            })}}
          </span>
          <info-badge
            :string-key="$t('reports.alarms-and-warnings.list.period-tooltip', {
              days: data.item.period
            })"
          />
        </span>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="primary"
            @click="prepareGenerateOpen(data.item.template)"
        >
          {{ $t('general.view') }}
        </b-button>
        <b-dropdown
          class="report-list__options-btn"
          right
          no-caret
          offset="-15"
          variant="link"
          boundary="window"
        >
          <template slot="button-content">
            <s-loader v-if="isReportLoading(data.item.id)"/>
            <i
              v-else
              aria-hidden="true"
              class="fas fa-ellipsis-v text-gray"
            />
          </template>
          <b-dropdown-item
            v-for="({
              label,
              itemClass,
              iconClass,
              click,
            }) in dropdownItems"
            :key="label"
            :class="itemClass"
            @click="click(data.item)"
          >
            <span
              class="report-list__option-icon text-center"
              :class="iconClass"
            />
            {{ label }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </s-table>
    <grid-paginator
      v-model="pageHandler"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalRows"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import ListAggregator from '@/components/listAggregator';
import AlarmsAndWarningsService from '@/domain/services/generalPanel/reports/alarmsAndWarnings';
import { alertModes } from '@/services/messages/alertService';
import periods from '@/domain/enums/periods';
import GenerateReportMixin from '@/domain/mixins/generalPanel/reports/generateReportMixin';
import AlarmsReportMixin from '@/domain/mixins/generalPanel/reports/alarmsReportMixin';
import { additionalFieldsEnum, classificationsEnum, statusEnum } from '@/domain/enums/moduleAlarmsAndWarnings';
import InfoBadge from '@/components/icon/InfoBadge';

export default {
  components: {
    GridPaginator,
    ListAggregator,
    InfoBadge,
  },
  mixins: [
    GenerateReportMixin,
    AlarmsReportMixin,
  ],
  data: () => ({
    multipleColumnsFlag: true,
    reportsFilter: {
      period: periods.SEVEN,
    },
    loadingDropdown: '',
    requiredFields: ['companiesIds', 'installationsIds', 'assetsIds', 'modulesIds', 'status', 'classifications'],
  }),
  props: {
    searchTerm: {
      type: String,
    },
    reports: {
      type: Array,
    },
    loading: {
      type: Boolean,
    },
    sortBy: {
      type: Object,
      default: () => ({}),
    },
    pagination: {
      type: Object,
      default: () => ({
        perPage: 200,
        currentPage: 1,
        totalRows: null,
      }),
    },
  },
  computed: {
    pageHandler: {
      get() {
        return this.pagination.currentPage;
      },
      set(newPage) {
        this.$emit('page-changed', newPage);
      },
    },
    dropdownItems() {
      return [
        {
          itemClass: 'report-list__option-export',
          iconClass: 'fas fa-file-excel',
          label: this.$t('general.export-xlsx'),
          click: alarmsAndWarnings => this.exportToXlsx(alarmsAndWarnings),
        },
        {
          itemClass: 'report-list__option-edit',
          iconClass: 'fas fa-pencil-alt',
          label: this.$t('reports.edit-report'),
          click: alarmsAndWarnings => this.editReport(alarmsAndWarnings),
        },
        {
          itemClass: 'report-list__option-delete',
          iconClass: 'fas fa-trash-alt',
          label: this.$t('reports.delete-report'),
          click: alarmsAndWarnings => this.delete(alarmsAndWarnings),
        },
      ];
    },
    tableHeadersAssets() {
      return [
        {
          key: 'name',
          label: this.$t('reports.modal.name'),
        },
        {
          key: 'typeAsset',
          label: this.$t('reports.modal.asset-type'),
        },
      ];
    },
    fields() {
      return [
        {
          key: 'name',
          sortable: true,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('reports.report-name'),
        },
        {
          key: 'installations',
          sortable: false,
          labelPath: 'installations',
          label: this.$t('entities.installation'),
          class: 'truncate report-list__cell-installation',
        },
        {
          key: 'assets',
          sortable: false,
          labelPath: 'assets',
          class: 'report-list__cell-asset',
          label: this.$t('entities.asset'),
        },
        {
          key: 'period',
          sortable: false,
          labelPath: 'period',
          class: 'report-list__cell-asset',
          label: this.$t('entities.period'),
        },
        {
          key: 'actions',
          sortable: false,
          labelPath: null,
          label: '',
          class: 'report-list__cell-actions',
        },
      ];
    },
  },
  methods: {
    isReportLoading(id) {
      return this.loadingDropdown === id;
    },
    editReport({ id }) {
      this.$router.push({
        name: 'reports.alarms-and-warnings.edit-report',
        params: {
          relatorioId: id,
        },
      });
    },
    assetTypeNameInternacionalization(typeAsset) {
      const assetTypes = {
        Transformador: 'transformer',
        Seccionador: 'disconnector',
        Disjuntor: 'circuit-breaker',
        Indefinido: 'undefined',
      };
      return this.$t(`reports.${assetTypes[typeAsset]}`);
    },
    showAllAssetsWithType(assets) {
      return assets.map(({ name, typeAsset }) => ({
        name,
        typeAsset: this.assetTypeNameInternacionalization(typeAsset),
      }));
    },
    isPeriodPersonalized(period) {
      return !period || period === periods.PERSONALIZED;
    },
    removeReport(id) {
      return AlarmsAndWarningsService.removeTemplate(id).then(() => {
        this.$pushAlert({
          message: 'reports.report-successfully-excluded',
          mode: alertModes.TOAST,
          type: 'success',
        });
        this.$emit('refresh-list');
      });
    },
    delete({ id }) {
      this.$confirmMessage({ code: 'MC042' })
        .accepted(() => this.removeReport(id));
    },
    sort(event) {
      this.$emit('sort-changed', event);
    },
    openReport(payload) {
      this.openAlarmReport(this.configurePayloadField(payload));
    },
    prepareGenerateOpen(payload) {
      const field = this.configurePayloadField(payload);
      this.generateReport(() => this.openAlarmReport(field));
    },
    getEnumAdditionalFields(fields) {
      return fields.map(field => additionalFieldsEnum[field]);
    },
    getEnumClassifications(classifications) {
      return classifications.map(classification => classificationsEnum[classification]);
    },
    getEnumStatus(itemStatusEnum) {
      const convertArray = itemStatusEnum.split(', ');
      return convertArray.map(item => statusEnum[item]);
    },
    exportToXlsx(payload) {
      this.loadingDropdown = payload.id;
      const field = this.configurePayloadField(payload.template);

      AlarmsAndWarningsService.exportHistoryExcel({ filter: field })
        .then(() => this.$pushAlert({ code: 'MS011' }))
        .finally(() => {
          this.loadingDropdown = '';
        });
    },
    configurePayloadField(payload) {
      const adjustPayload = {
        additionalFieldsEnum: this.getEnumAdditionalFields(payload.camposAdicionais),
        alarmsIds: payload.alarmesIds,
        assetsIds: payload.ativos.map(asset => asset.id),
        classifications: this.getEnumClassifications(payload.classificacoes),
        companiesIds: payload.empresas.map(company => company.id),
        installationsIds: payload.instalacoes.map(installation => installation.id),
        modulesIds: payload.modulos.map(module => module.id),
        period: payload.diasPeriodo,
        regionalIds: payload.regionais.map(regional => regional.id),
        status: this.getEnumStatus(payload.statusAlarme),
        initialDateTime: payload.dataInicial,
        finalDateTime: payload.dataFinal,
        initialDate: payload.dataInicial,
        finalDate: payload.dataFinal,
      };
      this.reportsFilter = adjustPayload;
      return adjustPayload;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.report-list
  width: 100%
  ::v-deep
    .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:hover,
    .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:focus
      text-decoration: none
  &__table
      flex: 1
      min-height: 48vh
  &__options-btn
    margin: -10px 0
  &__option-icon
    margin-right: 5px
    width: 1em
  ::v-deep &__option
    &-export a
      color: var(--color-primary)
    &-delete a
      color: var(--color-danger)
  ::v-deep &__cell
    &-report-name
      min-width: 100px
      width: 20%
    &-installations
      width: 25%
    &-asset-type
      width: 25%
    &-asset
      width: 10%
    &-actions
      min-width: 15em
      width: 15%
</style>
